import React, { useState, useCallback } from 'react';
import { generateCopyText } from './spectraUtils';
import { ANALYSIS_CATEGORIES } from './SpectraToolbar';

interface SpectraResultsProps {
  result: any | null;
  activeCategory: string | null;
  activeSubOption: string | null;
}

// ---- Help content ----

const HELP_CONTENT: Record<string, { title: string; what: string; when: string; read: string }> = {
  unpaired_t: {
    title: 'Unpaired t-test',
    what: 'Compares means of two independent groups to determine if they differ significantly. Assumes normally distributed data with equal variances.',
    when: 'Use when comparing two independent groups (e.g. Control vs Treated) with continuous outcome data that is approximately normally distributed.',
    read: 'A small p-value (<0.05) suggests the group means differ significantly. Cohen\'s d indicates effect magnitude: small (<0.2), medium (0.5), large (>0.8).',
  },
  paired_t: {
    title: 'Paired t-test',
    what: 'Compares means of two related measurements (before/after, same subject). Tests whether the mean difference is significantly different from zero.',
    when: 'Use when you have paired observations — e.g. the same samples measured at two timepoints, or matched pairs.',
    read: 'A significant result means the mean change from condition A to B is unlikely due to chance. The 95% CI of the difference tells you the plausible range.',
  },
  welch_t: {
    title: "Welch's t-test",
    what: 'Like the unpaired t-test but does NOT assume equal variances. More robust when group sizes or variances differ.',
    when: 'Use instead of the unpaired t-test when Levene\'s test suggests unequal variances, or when group sizes are unbalanced.',
    read: 'Interpretation is identical to the unpaired t-test. Welch\'s adjusts the degrees of freedom to account for variance differences.',
  },
  mann_whitney: {
    title: 'Mann-Whitney U test',
    what: 'Non-parametric test comparing the ranks of two independent groups. Does not assume normality.',
    when: 'Use when data is ordinal, or when Shapiro-Wilk indicates non-normal distributions, or with small sample sizes.',
    read: 'Tests whether one group tends to have larger values. The rank-biserial correlation (r) indicates effect size.',
  },
  one_way_anova: {
    title: 'One-way ANOVA',
    what: 'Tests whether the means of three or more groups differ significantly. Assumes normality and equal variances.',
    when: 'Use when comparing 3+ independent groups on a continuous outcome (e.g. vehicle, low dose, high dose).',
    read: 'A significant F-test means at least one group differs. Use post-hoc tests (Tukey) to identify which pairs differ.',
  },
  kruskal_wallis: {
    title: 'Kruskal-Wallis H test',
    what: 'Non-parametric alternative to one-way ANOVA. Compares ranks across 3+ independent groups.',
    when: 'Use when ANOVA assumptions are violated (non-normal data, unequal variances) with 3+ groups.',
    read: 'A significant H statistic means at least one group differs in central tendency. Follow up with Dunn\'s test.',
  },
  pearson: {
    title: 'Pearson correlation',
    what: 'Measures the linear relationship between two continuous variables. Returns r from -1 (perfect negative) to +1 (perfect positive).',
    when: 'Use when both variables are continuous and approximately normally distributed, and you expect a linear relationship.',
    read: 'r close to \u00B11 = strong linear relationship. R\u00B2 tells you the % of variance explained. p-value tests H0: r=0.',
  },
  spearman: {
    title: 'Spearman rank correlation',
    what: 'Non-parametric correlation measuring monotonic (not necessarily linear) relationships using ranked data.',
    when: 'Use when data is ordinal, non-normal, or the relationship is monotonic but not linear.',
    read: 'Interpretation similar to Pearson but based on ranks. Robust to outliers.',
  },
  linear_regression: {
    title: 'Linear Regression',
    what: 'Fits a line y = mx + b to your data and tests whether the slope differs from zero.',
    when: 'Use when you want to predict Y from X, or quantify the rate of change in Y per unit X.',
    read: 'Slope = change in Y per unit X. R\u00B2 = proportion of variance explained. The 95% CI for slope tells you the precision.',
  },
  descriptive: {
    title: 'Descriptive Statistics',
    what: 'Summarises your data: mean, median, SD, SEM, and tests for normality (Shapiro-Wilk).',
    when: 'Use as a first step to understand your data distribution before choosing a test.',
    read: 'Mean vs median divergence suggests skewness. Shapiro-Wilk p > 0.05 supports normality assumption.',
  },
};

const SpectraResults: React.FC<SpectraResultsProps> = ({ result, activeCategory, activeSubOption }) => {
  const [tab, setTab] = useState<'results' | 'help'>('results');
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    if (!result) return;
    const text = generateCopyText(result);
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [result]);

  const helpKey = activeSubOption || '';
  const help = HELP_CONTENT[helpKey];

  return (
    <div style={{
      width: '320px',
      minWidth: '320px',
      borderLeft: '1px solid var(--card-border)',
      background: 'var(--card-bg)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Tab switcher */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid var(--divider)',
        background: 'var(--hover-bg)',
      }}>
        {(['results', 'help'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              flex: 1,
              padding: '8px',
              border: 'none',
              borderBottom: tab === t ? '2px solid rgb(99, 102, 241)' : '2px solid transparent',
              background: 'transparent',
              color: tab === t ? 'rgb(129, 120, 248)' : 'var(--text-secondary)',
              fontSize: '12px',
              fontWeight: tab === t ? 600 : 400,
              cursor: 'pointer',
              textTransform: 'capitalize',
              transition: 'all 0.15s ease',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
        {tab === 'results' ? (
          result ? <ResultsPanel result={result} onCopy={handleCopy} copied={copied} /> : (
            <div style={{ color: 'var(--text-secondary)', fontSize: '12px', fontStyle: 'italic', textAlign: 'center', marginTop: '40px' }}>
              Run an analysis to see results
            </div>
          )
        ) : (
          help ? <HelpPanel help={help} /> : (
            <div style={{ color: 'var(--text-secondary)', fontSize: '12px', textAlign: 'center', marginTop: '40px' }}>
              <div style={{ fontStyle: 'italic', marginBottom: '16px' }}>Select an analysis type to see help</div>
              <div style={{ textAlign: 'left', fontSize: '11px', lineHeight: '1.6' }}>
                <strong style={{ color: 'var(--text-primary)' }}>Getting Started:</strong>
                <ol style={{ paddingLeft: '16px', margin: '8px 0' }}>
                  <li>Paste data from Excel (Ctrl+V)</li>
                  <li>Select an analysis category</li>
                  <li>Choose a specific test</li>
                  <li>Click "Run Analysis"</li>
                </ol>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

// ---- Results sub-panel ----

const ResultsPanel: React.FC<{ result: any; onCopy: () => void; copied: boolean }> = ({ result, onCopy, copied }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
    {/* Test name */}
    <div style={{
      fontSize: '14px',
      fontWeight: 600,
      color: 'var(--text-primary)',
      paddingBottom: '8px',
      borderBottom: '1px solid var(--divider)',
    }}>
      {result.test_name}
    </div>

    {/* Key statistics */}
    {result.statistic != null && (
      <KeyValue label="Test Statistic" value={result.statistic} />
    )}
    {result.p_value != null && (
      <KeyValue
        label="p-value"
        value={result.p_value}
        badge={result.p_value < 0.001 ? '***' : result.p_value < 0.01 ? '**' : result.p_value < 0.05 ? '*' : 'ns'}
        badgeColor={result.p_value < 0.05 ? '#10b981' : '#6b7280'}
      />
    )}
    {result.effect_size != null && (
      <KeyValue label="Effect Size" value={`${result.effect_size} (${result.effect_label || ''})`} />
    )}
    {result.r_value != null && (
      <>
        <KeyValue label="r" value={result.r_value} />
        <KeyValue label="R\u00B2" value={result.r_squared} />
      </>
    )}
    {result.slope != null && (
      <>
        <KeyValue label="Slope" value={result.slope} />
        <KeyValue label="Intercept" value={result.intercept} />
        <KeyValue label="Slope SE" value={result.regression_se} />
      </>
    )}
    {result.ci_95 && (
      <KeyValue label="95% CI" value={`[${result.ci_95[0]}, ${result.ci_95[1]}]`} />
    )}
    {result.skewness != null && (
      <>
        <KeyValue label="Skewness" value={result.skewness} />
        <KeyValue label="Kurtosis" value={result.kurtosis} />
      </>
    )}

    {/* Group stats */}
    {result.group_stats && result.group_stats.length > 0 && (
      <div>
        <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
          Group Statistics
        </div>
        <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--divider)' }}>
              <th style={{ textAlign: 'left', padding: '3px 4px', color: 'var(--text-secondary)', fontWeight: 500 }}>Group</th>
              <th style={{ textAlign: 'right', padding: '3px 4px', color: 'var(--text-secondary)', fontWeight: 500 }}>n</th>
              <th style={{ textAlign: 'right', padding: '3px 4px', color: 'var(--text-secondary)', fontWeight: 500 }}>Mean</th>
              <th style={{ textAlign: 'right', padding: '3px 4px', color: 'var(--text-secondary)', fontWeight: 500 }}>SD</th>
            </tr>
          </thead>
          <tbody>
            {result.group_stats.map((gs: any, i: number) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--divider)' }}>
                <td style={{ padding: '3px 4px', color: 'var(--text-primary)' }}>{i + 1}</td>
                <td style={{ padding: '3px 4px', textAlign: 'right', color: 'var(--text-primary)' }}>{gs.n}</td>
                <td style={{ padding: '3px 4px', textAlign: 'right', color: 'var(--text-primary)' }}>{gs.mean}</td>
                <td style={{ padding: '3px 4px', textAlign: 'right', color: 'var(--text-primary)' }}>{gs.std}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {/* Assumptions */}
    {result.assumptions && result.assumptions.length > 0 && (
      <div>
        <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
          Assumptions
        </div>
        {result.assumptions.map((a: any, i: number) => (
          <div key={i} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 0',
            fontSize: '11px',
          }}>
            <span style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: a.passed ? '#10b981' : '#f59e0b',
              flexShrink: 0,
            }} />
            <span style={{ color: 'var(--text-primary)' }}>{a.test_name}</span>
            <span style={{ color: 'var(--text-secondary)', fontSize: '10px' }}>
              {a.passed ? 'OK' : 'Violated'}
            </span>
          </div>
        ))}
      </div>
    )}

    {/* Post-hoc */}
    {result.post_hoc && result.post_hoc.length > 0 && (
      <div>
        <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
          Post-hoc Comparisons
        </div>
        {result.post_hoc.map((ph: any, i: number) => (
          <div key={i} style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '3px 0',
            fontSize: '11px',
            color: 'var(--text-primary)',
          }}>
            <span>Group {ph.group_a + 1} vs {ph.group_b + 1}</span>
            <span>
              p={ph.p_value}{' '}
              <span style={{ color: ph.significant ? '#10b981' : '#6b7280', fontWeight: 600 }}>
                {ph.label}
              </span>
            </span>
          </div>
        ))}
      </div>
    )}

    {/* Narrative */}
    {result.narrative && (
      <div style={{
        padding: '10px',
        borderRadius: '6px',
        background: 'rgba(99, 102, 241, 0.06)',
        border: '1px solid rgba(99, 102, 241, 0.15)',
        fontSize: '11px',
        lineHeight: '1.6',
        color: 'var(--text-primary)',
      }}>
        {result.narrative}
      </div>
    )}

    {/* Copy button */}
    <button
      onClick={onCopy}
      style={{
        padding: '8px',
        borderRadius: '6px',
        border: '1px solid var(--card-border)',
        background: copied ? 'rgba(16, 185, 129, 0.1)' : 'var(--hover-bg)',
        color: copied ? '#10b981' : 'var(--text-primary)',
        fontSize: '12px',
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
      }}
    >
      {copied ? 'Copied!' : 'Copy Results'}
    </button>
  </div>
);

// ---- Help sub-panel ----

const HelpPanel: React.FC<{ help: { title: string; what: string; when: string; read: string } }> = ({ help }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>
      {help.title}
    </div>

    <HelpSection title="What it does" content={help.what} />
    <HelpSection title="When to use it" content={help.when} />
    <HelpSection title="How to read results" content={help.read} />
  </div>
);

const HelpSection: React.FC<{ title: string; content: string }> = ({ title, content }) => (
  <div>
    <div style={{
      fontSize: '11px',
      fontWeight: 600,
      color: 'rgb(129, 120, 248)',
      marginBottom: '4px',
      textTransform: 'uppercase',
      letterSpacing: '0.03em',
    }}>
      {title}
    </div>
    <div style={{ fontSize: '12px', lineHeight: '1.6', color: 'var(--text-primary)' }}>
      {content}
    </div>
  </div>
);

// ---- Key-value row ----

const KeyValue: React.FC<{ label: string; value: any; badge?: string; badgeColor?: string }> = ({ label, value, badge, badgeColor }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '12px',
  }}>
    <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
    <span style={{ color: 'var(--text-primary)', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px' }}>
      {String(value)}
      {badge && (
        <span style={{
          padding: '1px 5px',
          borderRadius: '3px',
          fontSize: '10px',
          fontWeight: 700,
          background: (badgeColor || '#6b7280') + '22',
          color: badgeColor || '#6b7280',
        }}>
          {badge}
        </span>
      )}
    </span>
  </div>
);

export default SpectraResults;
